import { css } from '@emotion/react';
import { BNPLApi, VerticalOptions } from '@frontend/api-buy-now-pay-later';
import { http } from '@frontend/fetch';
import { Trans, useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { DropdownField, Heading, PrimaryButton, Text, TextLink, useFormField, useAlert } from '@frontend/design-system';
import { getVerticalListQueryKey } from '../../../utils/query-keys';

export type BuyNowPayLaterSignupProps = {
  updateStatus: () => void;
  locationId: string | undefined;
};
export const BuyNowPayLaterSignup = ({ updateStatus, locationId }: BuyNowPayLaterSignupProps) => {
  const { t } = useTranslation('payments');
  const verticalProps = useFormField({ type: 'dropdown', required: true });
  const alert = useAlert();

  const verticalTranslationMap = {
    'Car Dealerships': t('Car Dealerships'),
    'Car Services': t('Car Services'),
    'Dental/Orthodontics': t('Dental/Orthodontics'),
    'Home Services': t('Home Services'),
    'Jewelry and Watches': t('Jewelry and Watches'),
    Medical: t('Medical'),
    Medspa: t('Medspa'),
    'Motorsports Parts and Services': t('Motorsports Parts and Services'),
    'Optometry/Eyewear': t('Optometry/Eyewear'),
    Veterinary: t('Veterinary'),
  };

  const createAccount = async () => {
    const payload = {
      location: locationId,
      vertical: verticalProps.value,
    };
    try {
      const { url } = await BNPLApi.buyNowPayLater.onboard(payload, locationId ?? '');
      window.open(url!, '_blank');
      updateStatus();
    } catch (err) {
      if (http.isHttpError(err)) {
        // @ts-ignore: This is the new structure for error responses on this API but typescript thinks it needs to be a record. Type "any" or custom type does not fix it
        if (err.status === 403 && err.data && err.data?.error?.message.includes('not authorized')) {
          alert.error('Payment Admin role required');
        }
        return;
      }
      alert.error('There was a problem submitting your request.');
    }
  };

  const { data: verticalResponse } = useScopedQuery({
    queryKey: getVerticalListQueryKey(locationId),
    queryFn: () => BNPLApi.buyNowPayLater.getVerticals(locationId ?? ''),
  });

  return (
    <div
      css={css`
        max-width: ${theme.spacing(58)};
        min-width: ${theme.spacing(20)};
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-top: ${theme.spacing(6)};
      `}
    >
      <div>
        <Heading level={2}>{t('Activate Sunbit')}</Heading>
        <Trans t={t}>
          <Text color='light' size='medium'>
            Select your business vertical and click below to visit Sunbit and activate. Once you have activated, you
            will be able to offer buy now, pay-over-time from within Weave.
          </Text>
          <Text
            color='light'
            size='medium'
            css={css`
              margin-top: ${theme.spacing(1)};
            `}
          >
            If you already have a Sunbit account, select your business vertical and click Activate Sunbit to be
            redirected and connect your account to Weave.
          </Text>
        </Trans>
        <div
          css={css`
            display: flex;
            flex-flow: column;
            @media only screen and (min-width: 600px) {
              flex-flow: row;
            }
          `}
        >
          <DropdownField
            label={t('Business Vertical')}
            name='business_vertical'
            {...verticalProps}
            helperText={t('Please select the business vertical that closely matches your services')}
            data-trackingid='vertical-dropdown'
            css={css`
              width: 100%;
              min-width: ${theme.spacing(32)};
              margin-top: ${theme.spacing(3)};
            `}
          >
            {verticalResponse?.vertical?.map((value: string) => (
              <DropdownField.Option key={value} value={value}>
                {verticalTranslationMap[value as VerticalOptions]}
              </DropdownField.Option>
            ))}
            <DropdownField.Option key='notListed' value={'notListed'}>
              {t('My vertical is not listed')}
            </DropdownField.Option>
          </DropdownField>
          <PrimaryButton
            css={css`
              max-width: fit-content;
              min-width: ${theme.spacing(10)};
              margin-top: ${theme.spacing(2)};
              @media only screen and (min-width: 600px) {
                margin-top: ${theme.spacing(3.5)};
                margin-left: ${theme.spacing(2)};
              }
            `}
            disabled={!verticalProps.value || verticalProps.value === 'notListed'}
            onClick={createAccount}
          >
            {t('Activate Sunbit')}
          </PrimaryButton>
        </div>
      </div>
      {verticalProps.value === 'notListed' && (
        <div
          css={css`
            margin-top: ${theme.spacing(2)};
          `}
        >
          <Trans>
            <Text size='medium' color='error'>
              Sunbit is not currently supported for your vertical
            </Text>
          </Trans>
          <Trans>
            <TextLink
              css={css`
                align-self: start;
              `}
              onClick={() => window.open('https://feedback.getweave.com/', '_blank')}
            >
              Request my vertical
            </TextLink>
          </Trans>
        </div>
      )}
    </div>
  );
};
